import { Edit, LockOpen, LockOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  Link,
  MenuItem,
  Chip as MuiChip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField
} from '@mui/material'
import { ExplanationAccordion, LockStatusChip, SortableTableCell } from 'components'
import { Chip } from 'components/chips'
import { ListBody } from 'components/list'
import { useLayout } from 'context'
import * as controllers from 'controllers'
import { VehicleDto } from 'dtos'
import { useLoadingState, useQuery } from 'hooks'
import { DEBOUNCE_DELAY } from 'localConstants'
import moment from 'moment'
import { VehicleToDoListParameters } from 'parameters'
import { useEffect, useState } from 'react'
import { Link as ReactRouterLink, useNavigate } from 'react-router-dom'
import { formatDate, useDebounce } from 'utils'
import VehicleLockStatusDialog from './VehicleLockStatusDialog'
import { enqueueSnackbar } from 'notistack'

export default function WaitingOnTripToDoListDetails() {
  const layout = useLayout()
  const navigate = useNavigate()
  const query = useQuery()

  const [filterField, setFilterField] = useState<string>('')
  const [filterValue, setFilterValue] = useState<string>('')
  const [filterValueFrom, setFilterValueFrom] = useState<string>('')
  const [filterValueTo, setFilterValueTo] = useState<string>('')
  const [loadingState, setLoadingState] = useLoadingState({
    isGettingVehicles: false,
    isLockingVehicles: false,
    isUnlockingVehicles: false
  })
  const [parameters, setParameters] = useState<VehicleToDoListParameters>({
    page: 0,
    pageSize: 10,
    orderBy: 'createdDateTime',
    order: 'asc'
  })
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleDto[]>([])
  const [totalCount, setTotalCount] = useState<number>(0)
  const [vehicleLockStatusDialogOpen, setVehicleLockStatusDialogOpen] = useState<boolean>(false)
  const [vehicleLockStatusVehicle, setVehicleLockStatusVehicle] = useState<VehicleDto>(new VehicleDto())
  const [vehicles, setVehicles] = useState<VehicleDto[]>()

  const onChangeSortByDirection = (orderBy: string) => {
    setParameters(prev => ({
      ...parameters,
      orderBy,
      order: prev.orderBy === orderBy && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }

  const lockVehicles = () => {
    setLoadingState('isLockingVehicles', true)
    controllers
      .lockVehicles(selectedVehicles)
      .then(response => {
        enqueueSnackbar('Vehicles Locked Successfully!', { variant: 'success' })
        setSelectedVehicles([])
        getWaitingOnTripToDoList(parameters)
      })
      .finally(() => {
        setLoadingState('isLockingVehicles', false)
      })
  }

  const unlockVehicles = () => {
    setLoadingState('isUnlockingVehicles', true)
    controllers
      .unlockVehicles(selectedVehicles)
      .then(response => {
        enqueueSnackbar('Vehicles Unlocked Successfully!', { variant: 'success' })
        setSelectedVehicles([])
        getWaitingOnTripToDoList(parameters)
      })
      .finally(() => {
        setLoadingState('isUnlockingVehicles', false)
      })
  }

  const getWaitingOnTripToDoList = useDebounce((parameters: VehicleToDoListParameters) => {
    setLoadingState('isGettingVehicles', true)
    controllers.toDoLists
      .getWaitingOnTripToDoList(parameters)
      .then(response => {
        setTotalCount(response.totalCount ?? 0)
        setVehicles(response.value)
      })
      .finally(() => {
        setLoadingState('isGettingVehicles', false)
      })
  }, DEBOUNCE_DELAY)

  useEffect(() => {
    layout.setTitle('Waiting on Trip')

    return () => {
      layout.setTitle('')
    }
  }, [layout])

  useEffect(() => {
    layout.setIsLayoutLoading(
      loadingState.isGettingVehicles || loadingState.isLockingVehicles || loadingState.isUnlockingVehicles
    )
  }, [layout, loadingState.isGettingVehicles, loadingState.isLockingVehicles, loadingState.isUnlockingVehicles])

  useEffect(() => {
    getWaitingOnTripToDoList(parameters)
  }, [getWaitingOnTripToDoList, parameters])

  const importBranchGuid = query.get('importBranchGuid') ?? undefined
  useEffect(() => {
    setParameters({ ...parameters, importBranchGuid })
  }, [importBranchGuid])

  return (
    <>
      <VehicleLockStatusDialog
        onClose={() => {
          setVehicleLockStatusDialogOpen(false)
        }}
        open={vehicleLockStatusDialogOpen}
        vehicle={vehicleLockStatusVehicle}
      />

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
              <Grid item xs={12} sm={true} container spacing={1} alignItems='center'>
                <Grid item xs={12} sm='auto'>
                  <TextField
                    fullWidth
                    label='Filter Field'
                    onChange={e => {
                      setFilterField(e.target.value)
                      setFilterValue('')
                    }}
                    select
                    sx={{ minWidth: 200 }}
                    value={filterField || ''}
                  >
                    <MenuItem value='createdDateTime'>Created Date</MenuItem>

                    <MenuItem value='exporter'>Exporter</MenuItem>

                    <MenuItem value='vehicleType'>Vehicle Type</MenuItem>

                    <MenuItem value='vin'>VIN</MenuItem>

                    <MenuItem value='year'>Year</MenuItem>

                    <MenuItem value='make'>Make</MenuItem>

                    <MenuItem value='model'>Model</MenuItem>

                    <MenuItem value='importerStockNumber'>Importer Stock #</MenuItem>

                    <MenuItem value='inboundTripNumber'>Inbound Trip #</MenuItem>

                    <MenuItem value='papsNumber'>PAPS #</MenuItem>
                  </TextField>
                </Grid>

                {[
                  '',
                  'exporter',
                  'vehicleType',
                  'vin',
                  'year',
                  'make',
                  'model',
                  'importerStockNumber',
                  'inboundTripNumber',
                  'papsNumber'
                ].includes(filterField) && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      disabled={!filterField}
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      value={filterValue || ''}
                    />
                  </Grid>
                )}

                {filterField === 'createdDateTime' && (
                  <>
                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { max: filterValueTo ?? undefined } }}
                        label='Filter Date From'
                        onChange={e => {
                          setFilterValueFrom(e.target.value)
                        }}
                        type='date'
                        value={filterValueFrom}
                      />
                    </Grid>

                    <Grid item xs={12} sm='auto'>
                      <TextField
                        InputLabelProps={{ shrink: true }}
                        InputProps={{ inputProps: { min: filterValueFrom ?? undefined } }}
                        label='Filter Date To'
                        onChange={e => {
                          setFilterValueTo(e.target.value)
                        }}
                        type='date'
                        value={filterValueTo}
                      />
                    </Grid>
                  </>
                )}

                {filterField === 'canBeLocked' && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      fullWidth
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      select
                      sx={{ minWidth: 200 }}
                      value={filterValue || ''}
                    >
                      <MenuItem value='true'>
                        <Chip variant='green'>Can Be Locked</Chip>
                      </MenuItem>

                      <MenuItem value='false'>
                        <Chip variant='red'>Incomplete</Chip>
                      </MenuItem>
                    </TextField>
                  </Grid>
                )}

                {filterField === 'isLocked' && (
                  <Grid item xs={12} sm='auto'>
                    <TextField
                      fullWidth
                      label='Filter Value'
                      onChange={e => {
                        setFilterValue(e.target.value)
                      }}
                      select
                      sx={{ minWidth: 200 }}
                      value={filterValue || ''}
                    >
                      <MenuItem value='true'>
                        <Chip variant='green'>Complete</Chip>
                      </MenuItem>

                      <MenuItem value='false'>
                        <Chip variant='red'>Incomplete</Chip>
                      </MenuItem>
                    </TextField>
                  </Grid>
                )}

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='inherit'
                    disabled={
                      !filterField || (filterField === 'createdDateTime' ? !filterValueFrom || !filterValueTo : !filterValue)
                    }
                    variant='contained'
                    onClick={() => {
                      console.log('filterField', filterField)
                      console.log('filterValue', filterValue)
                      if (filterField === 'createdDateTime') {
                        setParameters({
                          ...parameters,
                          createdDateFrom: filterValueFrom,
                          createdDateTo: filterValueTo
                        })
                      } else {
                        setParameters({
                          ...parameters,
                          [filterField]: filterValue
                        })
                      }

                      setFilterField('')
                      setFilterValue('')
                      setFilterValueFrom('')
                      setFilterValueTo('')
                    }}
                  >
                    APPLY FILTER
                  </Button>
                </Grid>

                <Grid item xs={12} sm='auto'>
                  <Button
                    color='secondary'
                    disabled={
                      !Boolean(
                        parameters.createdDateFrom ||
                          parameters.createdDateTo ||
                          parameters.exporter ||
                          parameters.vehicleType ||
                          parameters.vin ||
                          parameters.year ||
                          parameters.make ||
                          parameters.model ||
                          parameters.importerStockNumber ||
                          parameters.inboundTripNumber ||
                          parameters.papsNumber ||
                          parameters.canBeLocked ||
                          parameters.isLocked
                      )
                    }
                    variant='text'
                    onClick={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined,
                        exporter: undefined,
                        vehicleType: undefined,
                        vin: undefined,
                        year: undefined,
                        make: undefined,
                        model: undefined,
                        importerStockNumber: undefined,
                        inboundTripNumber: undefined,
                        papsNumber: undefined,
                        canBeLocked: undefined,
                        isLocked: undefined
                      })
                    }}
                  >
                    RESET FILTERS
                  </Button>
                </Grid>
              </Grid>

              <Grid item xs={12} sm='auto' container alignItems='center' justifyContent='flex-end'>
                <Grid item>
                  <Button
                    color='primary'
                    disabled={selectedVehicles.length === 0}
                    endIcon={<LockOutlined />}
                    onClick={lockVehicles}
                    variant='text'
                  >
                    LOCK VEHICLE
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    color='primary'
                    disabled={selectedVehicles.length === 0}
                    endIcon={<LockOpen />}
                    onClick={unlockVehicles}
                    variant='text'
                  >
                    UNLOCK VEHICLE
                  </Button>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} container spacing={1} alignItems='center'>
              {parameters.createdDateFrom && parameters.createdDateTo && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Created Date from "${moment(parameters.createdDateFrom).format('M/D/yyyy')}" to "${moment(
                      parameters.createdDateTo
                    ).format('M/D/yyyy')}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        createdDateFrom: undefined,
                        createdDateTo: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.exporter && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Exporter contains "${parameters.exporter}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        exporter: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vehicleType && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Vehicle Type is ${parameters.vehicleType}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vehicleType: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.vin && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`VIN contains ${parameters.vin}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        vin: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.year && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Year is ${parameters.year}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        year: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.make && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Make contains ${parameters.make}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        make: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.model && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Model contains ${parameters.model}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        model: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.importerStockNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Importer Stock # contains ${parameters.importerStockNumber}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        model: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.inboundTripNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Inbound Trip# contains ${parameters.inboundTripNumber}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        inboundTripNumber: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.papsNumber && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`PAPS # contains ${parameters.papsNumber}`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        papsNumber: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.canBeLocked && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Lock Status is "${parameters.canBeLocked.toString() === 'true' ? 'Can Be Locked' : 'Incomplete'}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        canBeLocked: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}

              {parameters.isLocked && (
                <Grid item xs={12} sm='auto'>
                  <MuiChip
                    color='secondary'
                    label={`Locked is "${parameters.isLocked.toString() === 'true' ? 'Complete' : 'Incomplete'}"`}
                    onDelete={() => {
                      setParameters({
                        ...parameters,
                        isLocked: undefined
                      })
                    }}
                    variant='outlined'
                  />
                </Grid>
              )}
            </Grid>

            <Grid item xs={12}>
              <ListBody>
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <Table>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell padding='checkbox'>
                              <Checkbox
                                color='primary'
                                checked={selectedVehicles.length === totalCount}
                                indeterminate={selectedVehicles.length > 0 && selectedVehicles.length !== totalCount}
                                onChange={() => {
                                  if (selectedVehicles.length === totalCount) {
                                    setSelectedVehicles([])
                                  } else {
                                    setSelectedVehicles(vehicles ?? [])
                                  }
                                }}
                              />
                            </TableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='createdDateTime'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Created Date
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='exporter'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Exporter
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vehicleType'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Vehicle Type
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='vin'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              VIN
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='year'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Year
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='make'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Make
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='model'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Model
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='importerStockNumber'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Importer Stock#
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='inboundTripNumber'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Inbound Trip#
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='papsNumber'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              PAPS#
                            </SortableTableCell>

                            <SortableTableCell
                              onChangeSortByDirection={onChangeSortByDirection}
                              propertyName='isLocked'
                              sortBy={parameters.orderBy}
                              sortDirection={parameters.order}
                            >
                              Lock Status
                            </SortableTableCell>

                            <TableCell padding='checkbox'></TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {vehicles?.map((vehicle, index) => (
                            <TableRow key={vehicle.vin} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                              <TableCell padding='checkbox' sx={{ fontSize: '7.99pt' }}>
                                <Checkbox
                                  color='primary'
                                  checked={selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)}
                                  onChange={() => {
                                    if (selectedVehicles.some(v => v.vehicleGuid === vehicle.vehicleGuid)) {
                                      setSelectedVehicles(selectedVehicles.filter(v => v.vehicleGuid !== vehicle.vehicleGuid))
                                    } else {
                                      setSelectedVehicles([...selectedVehicles, vehicle])
                                    }
                                  }}
                                />
                              </TableCell>

                              <TableCell
                                sx={theme => ({
                                  color:
                                    moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4
                                      ? theme.palette.error.main
                                      : 'inherit',
                                  fontWeight:
                                    moment().diff(moment(formatDate(vehicle.createdDateTime)), 'days') >= 4 ? 'bold' : 'inherit'
                                })}
                              >
                                {formatDate(vehicle.createdDateTime)}
                              </TableCell>

                              <TableCell>{vehicle.exporterCompany?.name}</TableCell>

                              <TableCell>{vehicle.vehicleType?.name}</TableCell>

                              <TableCell>{vehicle.vin}</TableCell>

                              <TableCell>{vehicle.year}</TableCell>

                              <TableCell>{vehicle.make}</TableCell>

                              <TableCell>{vehicle.model}</TableCell>

                              <TableCell>{vehicle.importerStockNumber}</TableCell>

                              <TableCell>
                                <Link
                                  component={ReactRouterLink}
                                  to={`/inbound-trips/${vehicle.inboundTripPAPS?.inboundTrip?.inboundTripGuid}`}
                                >
                                  {vehicle.inboundTripPAPS?.inboundTrip?.inboundTripNumber}
                                </Link>
                              </TableCell>

                              <TableCell>
                                <Link
                                  component={ReactRouterLink}
                                  to={`/inbound-trips/${vehicle.inboundTripPAPS?.inboundTrip?.inboundTripGuid}/paps/${vehicle.inboundTripPAPS?.inboundTripPAPSGuid}/manage-vehicles`}
                                >
                                  {vehicle.inboundTripPAPS?.papsNumber}
                                </Link>
                              </TableCell>

                              <TableCell>
                                <LockStatusChip
                                  canBeLocked={vehicle.canBeLocked}
                                  isLocked={vehicle.isLocked}
                                  onClick={() => {
                                    setVehicleLockStatusVehicle(vehicle)
                                    setVehicleLockStatusDialogOpen(true)
                                  }}
                                />
                              </TableCell>

                              <TableCell padding='checkbox' size='small'>
                                <Button
                                  color='primary'
                                  endIcon={<Edit />}
                                  onClick={() => {
                                    navigate('/vehicles/' + vehicle.vehicleGuid)
                                  }}
                                  size='small'
                                  variant='text'
                                >
                                  EDIT
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>

                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[5, 10, 25]}
                              SelectProps={{ fullWidth: false }}
                              count={totalCount}
                              rowsPerPage={parameters.pageSize}
                              page={parameters.page}
                              onPageChange={(_, page) => {
                                setParameters({
                                  ...parameters,
                                  page
                                })
                              }}
                              onRowsPerPageChange={e => {
                                setParameters({
                                  ...parameters,
                                  page: 0,
                                  pageSize: e.target.value as unknown as number
                                })
                              }}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Table>
                </Box>
              </ListBody>
            </Grid>

            <Grid item xs={12}>
              <ExplanationAccordion variant='outlined'>
                || Vehicles appear in this to do list if the Inbound Trip the vehicle is in has been locked, but the vehicle has
                no Vehicle Check-In Photos. || The Waiting on Trip “To Do List” table column “Inbound Trip#” values are
                hyperlinks that link to trip's Inbound Trip details page. || The Waiting on Trip “To Do List” table column
                “PAPS#” values are hyperlinks that link to the PAPS's "Vehicles in PAPS" page. || The Waiting on Trip “To Do
                List” is a list screen intended for users on a PC. || If an Inbound Trip arrives but an expected vehicle is
                missing, users will contact the exporter/transporter and then cancel the vehicle if the vehicle is never coming
                or if it is going to be coming on a different PAPS/Inbound Trip. || If an unexpected vehicle arrives on an
                Inbound Trip, this vehicle will not be in VTS2 and will be resolved outside of the software. || If an expected
                vehicle arrives but its VIN plate does not belong to that vehicle, the user will navigate to that vehicle’s
                Vehicle Details and press “Cancel Import” for that vehicle. This involves unlocking the trip, unlocking the
                PAPS, removing the vehicle from the PAPS, and then pressing “CANCEL IMPORT” for that vehicle. || If an expected
                vehicle arrives but is missing its VIN plate, if the user can tell which expected vehicle it is that has arrived
                based on the vehicle’s description, the user will press “Cancel Import” for that vehicle. This involves
                unlocking the trip, unlocking the PAPS, removing the vehicle from the PAPS, and then pressing “CANCEL IMPORT”
                for that vehicle. ||
              </ExplanationAccordion>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  )
}
